import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import ApartmentFinder from './pages/ApartmentFinder';
import AdminPage from './pages/AdminPage';
import { ApartmentProvider } from './context/ApartmentContext';
import { Building2, List, Eye } from 'lucide-react';
import './App.css';
import { updateConstructionYears } from './firebase/migration';

const Navigation = styled.nav`
  /* Completely hidden on desktop */
  display: none;

  @media (max-width: 850px) {
    display: flex;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    padding: 16px 20px;
    min-height: 76px;
    height: 76px;
    justify-content: space-between;
    align-items: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    box-sizing: border-box;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
`;

const ViewToggleNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  min-height: 48px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  
  &:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35), 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
    transition: transform 0.1s ease;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(0, 0, 0, 0.05);
    }
  }
  
  @media (max-width: 850px) {
    display: none;
  }
`;

// Mobile View Toggle Button Component
const MobileViewToggle = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: flex;
    align-items: center;
  }
`;

const ViewToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  min-height: 48px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  
  &:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35), 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
    transition: transform 0.1s ease;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;





/* Remove unused navigation sections */

const LogoLink = styled.a`
  display: none;
  cursor: pointer;
  
  @media (max-width: 850px) {
    display: block;
    text-decoration: none;
  }
`;

const MobileLogo = styled.img`
  display: none;
  
  @media (max-width: 850px) {
    display: block;
    height: 50px;
    width: auto;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;



/* Cleaned up - removed unused desktop navigation components */

function App() {
  // Default to 'list' for better overview on mobile
  const [mobileView, setMobileView] = useState('list');
  const [viewToggleState, setViewToggleState] = useState<{
    isRealView: boolean;
    handleViewSwitch: () => void;
    standardImagesLoaded: boolean;
    realImagesLoaded: boolean;
  } | null>(null);

  // Make migration function available in browser console
  useEffect(() => {
    (window as any).updateConstructionYears = updateConstructionYears;
    console.log('🔧 Migration tools loaded. Run updateConstructionYears() in console to update construction years.');
  }, []);

  // Toggle function between viewer and list
  const toggleMobileView = () => {
    setMobileView(mobileView === 'viewer' ? 'list' : 'viewer');
  };

  // Handle view toggle state from ApartmentFinder
  const handleViewToggle = useCallback((isRealView: boolean, handleViewSwitch: () => void, standardImagesLoaded: boolean, realImagesLoaded: boolean) => {
    setViewToggleState({ isRealView, handleViewSwitch, standardImagesLoaded, realImagesLoaded });
  }, []);

  // Get button content based on current view
  const getToggleButtonContent = () => {
    if (mobileView === 'viewer') {
      return {
        icon: <List size={16} />,
        text: 'Wohnungsliste'
      };
    } else {
      return {
        icon: <Building2 size={16} />,
        text: '360°'
      };
    }
  };

  const toggleContent = getToggleButtonContent();

  return (
    <ApartmentProvider>
      <Router>
        <div className="App">
          <Navigation>
            <ViewToggleNavButton 
              onClick={viewToggleState?.handleViewSwitch}
              disabled={!viewToggleState || (viewToggleState.isRealView && !viewToggleState.standardImagesLoaded) || (!viewToggleState.isRealView && !viewToggleState.realImagesLoaded)}
            >
              <Eye size={16} />
              {viewToggleState?.isRealView ? 'Nah' : 'Normal'}
            </ViewToggleNavButton>
            
            <LogoLink href="https://www.wildbach-hinwil.ch" target="_blank" rel="noopener noreferrer">
              <MobileLogo 
                src="/logo-1.svg" 
                alt="Wildbach Logo" 
              />
            </LogoLink>
            
            <MobileViewToggle>
              <ViewToggleButton onClick={toggleMobileView}>
                {toggleContent.icon}
                {toggleContent.text}
              </ViewToggleButton>
            </MobileViewToggle>
          </Navigation>
          
          <Routes>
            <Route path="/" element={<ApartmentFinder mobileView={mobileView} onMobileViewChange={setMobileView} onViewToggle={handleViewToggle} />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </div>
      </Router>
    </ApartmentProvider>
  );
}

export default App;
